import { useEffect, useMemo, useRef, useState } from "react";
import { Palette, Type, Truck, Monitor, CreditCard, Lock, Search, X, Check } from "lucide-react";

export const SETTINGS_TAB_KEY = "bk_admin_settings_tab";

export const SETTINGS_TABS = [
  { key: "look", label: "Look & Feel", Icon: Palette, blurb: "Themes, colors, background, homepage photos & videos" },
  { key: "content", label: "Text & Content", Icon: Type, blurb: "Every word customers read — hero, about, rewards, checkout" },
  { key: "ordering", label: "Truck & Ordering", Icon: Truck, blurb: "Phone, hours, location, tax, free drinks, menu categories" },
  { key: "register", label: "Register & Kitchen", Icon: Monitor, blurb: "POS layout, kitchen ticket colors, card reader tips" },
  { key: "square", label: "Square & Payments", Icon: CreditCard, blurb: "Connection health and failed payments" },
  { key: "security", label: "Security", Icon: Lock, blurb: "Admin password and cashier passcode" },
];

const readSaved = () => {
  const v = localStorage.getItem(SETTINGS_TAB_KEY);
  return SETTINGS_TABS.some((t) => t.key === v) ? v : "look";
};

/** Sub-tabbed settings shell with a "find a setting" search.
 *  `sections`: [{ id, tab, title, keywords, node }] */
export function SettingsShell({ sections, jumpTo, dirty, saving, onSave, onDiscard }) {
  const [sub, setSub] = useState(readSaved);
  const [query, setQuery] = useState("");
  const scrollTargetRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(SETTINGS_TAB_KEY, sub);
    document.querySelector(`[data-testid="settings-subtab-${sub}"]`)?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [sub]);

  useEffect(() => {
    if (jumpTo && SETTINGS_TABS.some((t) => t.key === jumpTo)) { setSub(jumpTo); setQuery(""); }
  }, [jumpTo]);

  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!q) return null;
    const words = q.split(/\s+/);
    return sections.filter((s) => {
      const hay = `${s.title} ${s.keywords || ""}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [q, sections]);

  const jump = (s) => {
    setSub(s.tab);
    setQuery("");
    scrollTargetRef.current = s.id;
  };

  useEffect(() => {
    if (!scrollTargetRef.current) return;
    const el = document.getElementById(`setting-${scrollTargetRef.current}`);
    scrollTargetRef.current = null;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("setting-flash");
      setTimeout(() => el.classList.remove("setting-flash"), 1600);
    }
  }, [sub, query]);

  const current = SETTINGS_TABS.find((t) => t.key === sub);
  const visible = sections.filter((s) => s.tab === sub);

  return (
    <div className="space-y-4 max-w-3xl mx-auto pb-24" data-testid="settings-shell">
      <label className="relative block">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a setting… (e.g. tax, password, theme, hours)"
          data-testid="settings-search-input"
          className="w-full pl-9 pr-9 py-3 rounded-2xl border-2 border-stone-200 bg-white text-sm outline-none focus:border-brand-red"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            aria-label="Clear search"
            data-testid="settings-search-clear"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </label>

      {results ? (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="settings-search-results">
          {results.length === 0 ? (
            <div className="p-5 text-sm text-stone-500" data-testid="settings-search-empty">
              Nothing matches "{query}". Try another word.
            </div>
          ) : (
            results.map((s) => {
              const t = SETTINGS_TABS.find((x) => x.key === s.tab);
              return (
                <button
                  key={s.id}
                  onClick={() => jump(s)}
                  data-testid={`settings-search-result-${s.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left border-b border-stone-100 last:border-0 hover:bg-stone-50"
                >
                  <t.Icon className="w-4 h-4 text-brand-red shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-stone-800 truncate">{s.title}</div>
                    <div className="text-[11px] text-stone-400 uppercase tracking-widest">{t.label}</div>
                  </div>
                  <span className="text-xs text-stone-400">Open →</span>
                </button>
              );
            })
          )}
        </div>
      ) : (
        <>
          <div
            className="flex gap-2 overflow-x-auto no-scrollbar -mx-3 px-3 sm:mx-0 sm:px-0 pb-1"
            style={{ scrollbarWidth: "none" }}
            data-testid="settings-subtabs"
          >
            {SETTINGS_TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setSub(key)}
                data-testid={`settings-subtab-${key}`}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest border-2 transition-colors ${
                  sub === key
                    ? "bg-brand-red border-brand-red text-white"
                    : "bg-white border-stone-200 text-stone-600 hover:border-brand-red/50"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          <div className="px-1">
            <div className="font-display text-brand-red text-2xl leading-tight">{current.label}</div>
            <div className="text-xs text-stone-500">{current.blurb}</div>
          </div>

          <div className="space-y-5" data-testid={`settings-panel-${sub}`}>
            {visible.map((s) => (
              <div key={s.id} id={`setting-${s.id}`} className="scroll-mt-4 rounded-2xl transition-shadow">
                {s.node}
              </div>
            ))}
          </div>
        </>
      )}

      {dirty && (
        <div
          className="sticky bottom-3 z-20 bg-stone-900 text-white rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl"
          data-testid="settings-save-bar"
        >
          <div className="flex-1 text-xs font-bold whitespace-nowrap">Unsaved changes</div>
          <button
            onClick={onDiscard}
            disabled={saving}
            data-testid="site-reset-btn"
            className="px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold text-white/80 hover:bg-white/10 disabled:opacity-40"
          >
            Discard
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="site-save-btn"
            className="bg-brand-yellow text-stone-900 px-5 py-2 rounded-full text-xs uppercase tracking-widest font-black hover:brightness-105 disabled:opacity-50 flex items-center gap-2"
          >
            <Check className="w-3.5 h-3.5" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}
